import { useLocation } from "react-router-dom";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import { pathToRegexp } from "path-to-regexp";

export enum TIER_LIST_PAGES {
  DEFAULT_TIER_LIST = "tier-list",
  TOP_TIER_LIST = "top-lane-tier-list",
  JUNGLE_TIER_LIST = "jungle-tier-list",
  MID_TIER_LIST = "mid-lane-tier-list",
  ADC_TIER_LIST = "adc-tier-list",
  SUPPORT_TIER_LIST = "support-tier-list",
  DUO_TIER_LIST = "duo-tier-list",
  COMBAT_TIER_LIST = "combat",
  OBJECTIVES_TIER_LIST = "objectives",
}

export enum SPECIAL_TIER_LIST_PAGES {
  // URF_TIER_LIST = "urf-tier-list",
  CLASSIC_TIER_LIST = "classic-tier-list",
  PICK_URF_TIER_LIST = "urf-tier-list",
  ARURF_TIER_LIST = "arurf-tier-list",
  ARAM_TIER_LIST = "aram-tier-list",
  ARAM_MAYHEM_TIER_LIST = "aram-mayhem-tier-list",
  NEXUS_BLITZ_TIER_LIST = "nexus-blitz-tier-list",
  ONE_FOR_ALL_TIER_LIST = "one-for-all-tier-list",
  ARENA_TIER_LIST = "arena-tier-list",
  ARENA_DUO_TIER_LIST = "arena-duo-tier-list",
}

// ------------------------------------------------------------------------------------------------------------
// TIER LIST PAGES BY URL
// ------------------------------------------------------------------------------------------------------------

export const tierListPageUrls = {
  [TIER_LIST_PAGES.DEFAULT_TIER_LIST]: AppRoutes.DEFAULT_TIER_LIST,
  [TIER_LIST_PAGES.TOP_TIER_LIST]: AppRoutes.TOP_TIER_LIST,
  [TIER_LIST_PAGES.JUNGLE_TIER_LIST]: AppRoutes.JUNGLE_TIER_LIST,
  [TIER_LIST_PAGES.MID_TIER_LIST]: AppRoutes.MID_TIER_LIST,
  [TIER_LIST_PAGES.ADC_TIER_LIST]: AppRoutes.ADC_TIER_LIST,
  [TIER_LIST_PAGES.SUPPORT_TIER_LIST]: AppRoutes.SUPPORT_TIER_LIST,
  [SPECIAL_TIER_LIST_PAGES.CLASSIC_TIER_LIST]: AppRoutes.CLASSIC_TIER_LIST,
  [SPECIAL_TIER_LIST_PAGES.PICK_URF_TIER_LIST]: AppRoutes.PICK_URF_TIER_LIST,
  [SPECIAL_TIER_LIST_PAGES.ARURF_TIER_LIST]: AppRoutes.ARURF_TIER_LIST,
  [SPECIAL_TIER_LIST_PAGES.ARAM_TIER_LIST]: AppRoutes.ARAM_TIER_LIST,
  [SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST]: AppRoutes.ARAM_MAYHEM_TIER_LIST,
  [SPECIAL_TIER_LIST_PAGES.NEXUS_BLITZ_TIER_LIST]: AppRoutes.NEXUS_BLITZ_TIER_LIST,
  [SPECIAL_TIER_LIST_PAGES.ONE_FOR_ALL_TIER_LIST]: AppRoutes.ONE_FOR_ALL_TIER_LIST,
  [SPECIAL_TIER_LIST_PAGES.ARENA_TIER_LIST]: AppRoutes.ARENA_TIER_LIST,
  [SPECIAL_TIER_LIST_PAGES.ARENA_DUO_TIER_LIST]: AppRoutes.ARENA_DUO_TIER_LIST,
  [TIER_LIST_PAGES.DUO_TIER_LIST]: AppRoutes.DUO_TIER_LIST,
  [TIER_LIST_PAGES.COMBAT_TIER_LIST]: AppRoutes.COMBAT_TIER_LIST,
  [TIER_LIST_PAGES.OBJECTIVES_TIER_LIST]: AppRoutes.OBJECTIVES_TIER_LIST,
} as const;

// Which tier list a pathname is, or undefined if it is not one of ours.
//
// The distinction matters because the tier-list routes include the catch-all
// `/lol/:gameMode-tier-list`, which matches any mode-shaped segment — real
// ("aram"), retired ("aram-mayhem-augment"), or nonsense ("duo1"). Only the
// table below says which of those is a page we actually serve.
export function getTierListPageFromPathname(pathname: string) {
  const foundPage = Object.entries(tierListPageUrls).find(([, path]) => pathToRegexp(path).exec(pathname));
  return foundPage?.[0] as keyof typeof tierListPageUrls | undefined;
}

// True only for a pathname this app has a tier list for. Route-level guard, so
// an unknown mode can render a 404 without first mounting a page whose hooks
// would fetch tier-list data for it.
export function useIsKnownTierListPage() {
  const { pathname } = useLocation();
  return !!getTierListPageFromPathname(pathname);
}

export function useTierListPage() {
  const location = useLocation();
  const { pathname } = location;

  // Never `undefined`. The return type has always claimed a real page id, but a
  // miss used to return undefined behind the cast, and every consumer indexes
  // something by it — `validParams[page]`, `tierListTitles[page]`,
  // `useTierList(page)`. The first of those to run then reads a property of
  // undefined and takes the whole page down with it.
  //
  // The route guard above is what keeps an unknown mode from reaching a page at
  // all; this fallback is the backstop for the callers that run `useTierListPage`
  // off a tier-list route entirely, such as ChampionLink on a champion page.
  return getTierListPageFromPathname(pathname) || (TIER_LIST_PAGES.DEFAULT_TIER_LIST as keyof typeof tierListPageUrls);
}
